import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseApiService, PagedResponse } from '../base/base-api.service';

export interface InvoiceFilterRequest {
    page?: number;
    pageSize?: number;
    status?: string;
    customerId?: string;
    source?: string;
}

export interface InvoiceItem {
    id: string;
    description: string;
    amount: number;
    sortOrder: number;
}

export interface Invoice {
    id: string;
    marinaId: string;
    leaseId: string;
    customerId: string;
    vesselId?: string;
    boatName?: string;
    invoiceNumber: string;
    /** Snapshot of payment terms at issue; optional on older records. */
    paymentTerms?: string | null;
    source: string;
    status: string; // Draft, Sent, Paid, Overdue, Cancelled, Refunded
    dueDate: string;
    subtotal: number;
    taxAmount: number;
    totalAmount: number;
    balanceRemaining: number;
    createdAt: string;
    items: InvoiceItem[];
    payments: InvoicePayment[];

    // ---- Stripe references: ADMIN SURFACES ONLY ----
    // Never render these to a customer. Customer screens use CustomerInvoice below, which has no
    // place for them by construction.
    stripeAccountId?: string | null;
    stripeInvoiceId?: string | null;
    /** Stripe's own invoice number, e.g. SWFSR06O-0003 — what Stripe support quotes. */
    stripeInvoiceNumber?: string | null;
    stripeSubscriptionId?: string | null;
    stripeStatus?: string | null;
    stripeCollectionMethod?: string | null;
    /** Null for automatically-charged invoices, which have no customer deadline. */
    stripeDueDate?: string | null;
    /** Cash Stripe actually collected, in minor units. Can be 0 on a paid invoice. */
    stripeAmountPaidMinor?: number | null;
    stripeTotalMinor?: number | null;
    servicePeriodStart?: string | null;
    servicePeriodEnd?: string | null;
    settlements?: InvoiceSettlement[];
}

/** Non-cash settlement against an invoice: credit applied, or an amount carried forward. */
export interface InvoiceSettlement {
    kind: 'CreditApplied' | 'CarriedForward' | 'Reversal' | 'Other';
    amountMinor: number;
    currency: string;
    /** Stripe's own type verbatim, retained even when unclassified. */
    stripeType?: string | null;
    stripeBalanceTransactionId?: string | null;
    stripeCreated: string;
}

/**
 * An invoice as a customer may see it — the response shape of `GET invoices/me`.
 *
 * A separate type from {@link Invoice} on purpose: adding a field to the admin shape must not be
 * able to leak it to a customer screen.
 */
export interface CustomerInvoice {
    id: string;
    invoiceNumber: string;
    boatName?: string | null;
    source: string;
    status: string;
    paymentTerms?: string | null;
    /** Null when Stripe charges the card automatically. Render as "—", never as a date. */
    dueDate: string | null;
    /** True when the card is charged automatically, so no action is expected. */
    isAutomaticPayment: boolean;
    servicePeriodStart?: string | null;
    servicePeriodEnd?: string | null;
    subtotal: number;
    taxAmount: number;
    totalAmount: number;
    balanceRemaining: number;
    /** Account credit applied to this invoice. A settlement, not a charge. */
    creditApplied: number;
    /** Moved onto the account balance instead of charged; appears on the next invoice. */
    carriedForward: number;
    createdAt: string;
    items: CustomerInvoiceItem[];
    payments: CustomerInvoicePayment[];
}

export interface CustomerInvoiceItem {
    description: string;
    quantity: number;
    unitPrice: number;
    amount: number;
}

export interface CustomerInvoicePayment {
    amount: number;
    method?: string | null;
    transactionDate: string;
    status?: string | null;
}

export interface InvoicePayment {
    id: string;
    invoiceId?: string | null;
    leaseId: string;
    amount: number;
    type?: string;
    method?: string;
    transactionDate: string;
    status?: string;
    referenceNo?: string;
    receiptUrl?: string;
    createdAt: string;
    updatedAt: string;
}

export interface CreateInvoiceItemRequest {
    description: string;
    amount: number;
}

export interface CreateInvoiceRequest {
    customerId: string;
    vesselId?: string;
    source: string;
    dueDate: string;
    items: CreateInvoiceItemRequest[];
    taxRate: number;
}

export interface RecordPaymentRequest {
    amount: number;
    method: string;
    referenceNo?: string;
}

@Injectable({
    providedIn: 'root',
})
export class InvoiceService extends BaseApiService {
    constructor(http: HttpClient) {
        super(http);
    }

    /**
     * Get all invoices for a lease contract
     */
    getByLeaseId(leaseId: string): Observable<Invoice[]> {
        return this.get<Invoice[]>(`leases/${leaseId}/invoices`);
    }

    /**
     * Get a specific invoice by ID
     */
    getById(invoiceId: string): Observable<Invoice> {
        return this.get<Invoice>(`invoices/${invoiceId}`);
    }

    /**
     * Create a new invoice for a lease
     */
    create(leaseId: string, request: CreateInvoiceRequest): Observable<Invoice> {
        return this.post<Invoice>(`leases/${leaseId}/invoices`, request);
    }

    /**
     * Generate first invoice when contract is activated
     */
    generateFirstInvoice(leaseId: string): Observable<Invoice> {
        return this.post<Invoice>(`leases/${leaseId}/invoices/generate-first`, {});
    }

    /**
     * Record a payment against an invoice
     */
    recordPayment(invoiceId: string, request: RecordPaymentRequest): Observable<Invoice> {
        return this.post<Invoice>(`invoices/${invoiceId}/record-payment`, request);
    }

    /**
     * Download invoice PDF
     */
    downloadPdf(invoiceId: string): Observable<Blob> {
        return this.http.get(`${this.baseUrl}/invoices/${invoiceId}/pdf`, {
            responseType: 'blob'
        });
    }

    /**
     * Email invoice PDF to customer
     */
    emailToCustomer(invoiceId: string): Observable<{ message: string; email: string }> {
        return this.post<{ message: string; email: string }>(`invoices/${invoiceId}/email`, {});
    }

    /**
     * Create a Stripe Checkout Session for an invoice (Step 1: Admin test payment)
     */
    createCheckoutSession(invoiceId: string, returnUrl: string): Observable<{ url: string; sessionId: string }> {
        return this.post<{ url: string; sessionId: string }>(`invoices/${invoiceId}/stripe/checkout`, { returnUrl });
    }

    /**
     * Get all invoices for the current customer
     */
    getMyInvoices(): Observable<CustomerInvoice[]> {
        return this.get<CustomerInvoice[]>('invoices/me');
    }

    /**
     * Get all invoices for a specific customer (Admin/Staff)
     */
    getByCustomerId(customerId: string): Observable<Invoice[]> {
        return this.get<Invoice[]>(`customer/${customerId}`);
    }

    /**
     * Verify a Stripe Checkout Session
     */
    verifySession(sessionId: string): Observable<InvoicePayment> {
        return this.post<InvoicePayment>(`invoices/stripe/verify-session/${sessionId}`, {});
    }

    /**
     * Pay an invoice using the customer's saved default payment method (Step 3: Client portal)
     */
    payWithSavedMethod(invoiceId: string): Observable<InvoicePayment> {
        return this.post<InvoicePayment>(`invoices/${invoiceId}/stripe/pay`, {});
    }

    /**
     * Admin-initiated payment: Charge invoice using customer's saved payment method
     */
    payWithSavedMethodAdmin(invoiceId: string): Observable<InvoicePayment> {
        return this.post<InvoicePayment>(`invoices/${invoiceId}/stripe/pay-admin`, {});
    }
    /**
     * Get invoice receipt HTML
     */
    getReceipt(invoiceId: string): Observable<Blob> {
        return this.http.get(`${this.baseUrl}/invoices/${invoiceId}/receipt`, {
            responseType: 'blob'
        });
    }

    /**
     * Get invoices with filtering. Status supports virtual filters: OPEN (Draft+Sent), OVERDUE,
     * PAID, as well as raw status values (Draft, Sent, Paid, Overdue, Cancelled, Refunded).
     */
    getAllFiltered(request?: InvoiceFilterRequest): Observable<PagedResponse<Invoice>> {
        let params = new HttpParams();
        if (request?.page != null) params = params.set('page', String(request.page));
        if (request?.pageSize != null) params = params.set('pageSize', String(request.pageSize));
        if (request?.status) params = params.set('status', request.status);
        if (request?.customerId) params = params.set('customerId', request.customerId);
        if (request?.source) params = params.set('source', request.source);
        return this.get<PagedResponse<Invoice>>('invoices', params);
    }
}
